"use client";

import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";

import { Loading, MessageBubble, MessageDateDivider } from "components";
import { GetMessagesQuery } from "graphql/type.ts";
import { useToast } from "hooks/useToast.tsx";
import { DateMessage } from "lib/types/talkroom";
import { InfiniteKeyedMutator } from "types/InfiniteKeyedMutator";

const TalkRoom = ({
  result: { messages, hasMore, loadNext, isValidating, isError, mutate },
  applicationId,
}: {
  result: {
    messages: DateMessage[];
    hasMore: boolean;
    isLoading: boolean;
    isValidating: boolean;
    mutate: InfiniteKeyedMutator<GetMessagesQuery[]>;
    isError: boolean;
    loadNext: () => void;
  };
  applicationId: string;
}) => {
  const { showToast } = useToast();
  const search = useSearchParams();
  const isJump = search.get("isJump") === "true";
  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading: isValidating,
    hasNextPage: hasMore,
    onLoadMore: loadNext,
    disabled: isError,
    rootMargin: "40px 0px 0px 0px",
  });

  const scrollableRootRef = useRef<HTMLDivElement | null>(null);
  const lastScrollDistanceToBottomRef = useRef<number | undefined>(undefined);

  // Jump to bottom when isJump is true, it helps user to see the latest message after sending a message or opening the chat from dashboard
  useEffect(() => {
    if (isJump && typeof window !== "undefined") {
      window.scrollTo(0, window.document.body.scrollHeight);
    }
  }, [isJump]);

  useEffect(() => {
    const scrollableRoot = scrollableRootRef.current;
    const lastScrollDistanceToBottom = lastScrollDistanceToBottomRef.current ?? 0;

    if (scrollableRoot) {
      scrollableRoot.scrollTop = scrollableRoot.scrollHeight - lastScrollDistanceToBottom;
    }
  }, [messages, rootRef, scrollableRootRef.current?.scrollHeight]);

  const rootRefSetter = useCallback(
    (node: HTMLDivElement) => {
      rootRef(node);
      scrollableRootRef.current = node;
    },
    [rootRef]
  );

  const handleRootScroll = useCallback(() => {
    const rootNode = scrollableRootRef.current;
    if (rootNode) {
      lastScrollDistanceToBottomRef.current = rootNode.scrollHeight - rootNode.scrollTop;
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      mutate();
    }, 5000);

    return () => clearInterval(interval);
  }, [mutate]);

  useEffect(() => {
    if (isError) {
      showToast("更新できませんでした");
    }
  }, [isError, showToast]);

  return (
    <div>
      <div
        className="-mr-10 h-[1030px] min-w-fit overflow-y-scroll pr-10"
        ref={rootRefSetter}
        onScroll={handleRootScroll}
      >
        <ul className="list-none">
          {hasMore && (
            <div ref={infiniteRef}>
              <li>
                <Loading />
              </li>
            </div>
          )}
          {messages?.map((dayMessage: DateMessage) => (
            <li key={dayMessage.key} className="mt-[30px] flex flex-col gap-[30px]">
              <MessageDateDivider date={dayMessage.date} />
              {dayMessage.messages.map((message) => (
                <MessageBubble key={message.id} mutate={mutate} message={message} applicationId={applicationId} />
              ))}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default TalkRoom;
